import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { SetModuleKeyValue } from 'app/library/module/actions/set-module-key-value.action';
import { IModuleQuery } from 'app/library/module/interfaces/module.query.interface';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { IEntityApiService } from 'app/projects/entity/src/lib/interfaces/entity.service.interface';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICreateGradingSchemeModel } from '../interfaces/create-grading-scheme.interface.model';
import { IGradeQuery } from '../interfaces/grade.query.interface';
import { IGradingSchemeQuery } from '../interfaces/grading-scheme.query.interface';
import { GradingSchemeCommand } from '../models/grading-scheme.command.model';
import { GradingScheme } from '../models/grading-scheme.model';

interface GradingSchemeApiServiceRead {
    grading_scheme: IGradingSchemeQuery;
    module: IModuleQuery;
    rule: string;
}

@Injectable()
export class GradingSchemeApiService extends EntityService<IGradingSchemeQuery> implements IEntityApiService<GradingScheme> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) environment: IEnvironment, private _store: Store, private _translateService: TranslateService) {
        super(http, environment.apiUrl, 'grading-schemes');
    }

    private _translateMapper(obj: { [key: string]: any }): any {
        const toReturn = {};

        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === 'string') {
                toReturn[key] = obj[key] ? this._translateService.instant(obj[key]) : '';
            } else {
                toReturn[key] = obj[key];
            }
        });

        return toReturn;
    }

    private _newGradingScheme(query: IGradingSchemeQuery, rule?: string): GradingScheme {
        const translatedQuery: IGradingSchemeQuery = this._translateMapper(query);

        return Object.assign(
            new GradingScheme({
                ...translatedQuery,
                grades: query.grades.map((gradeQuery) => {
                    return this._translateMapper(gradeQuery) as IGradeQuery;
                }),
            }),
            {
                rule,
            }
        );
    }

    getAll(): Observable<GradingScheme[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => this._newGradingScheme(query))));
    }

    create(entity: GradingScheme): Observable<GradingScheme> {
        throw new Error('Method not implemented.');
    }

    read(id: string): Observable<GradingScheme> {
        throw new Error('Method not implemented.');
    }

    update(entity: GradingScheme): Observable<GradingScheme> {
        throw new Error('Method not implemented.');
    }

    delete(id: string): Observable<any> {
        throw new Error('Method not implemented.');
    }

    fetchGradingSchemeByModuleId(moduleId: string): Observable<any> {
        return this.http.get<GradingSchemeApiServiceRead>(`${this.apiUrl}/modules/${moduleId}/grading-scheme`).pipe(
            map((query) => {
                if (!query) {
                    return null;
                }

                this._store.dispatch(new SetModuleKeyValue({ moduleId, key: 'gradingSchemeId', value: query.grading_scheme.id.toString() }));
                return this._newGradingScheme(query.grading_scheme, query.rule);
            })
        );
    }

    setGradingSchemeToModule({ moduleId, gradingScheme }: ICreateGradingSchemeModel): Observable<any> {
        return this.http.post<GradingSchemeApiServiceRead>(`${this.apiUrl}/modules/${moduleId}/grading-scheme`, new GradingSchemeCommand(gradingScheme)).pipe(
            map((query) => {
                this._store.dispatch(new SetModuleKeyValue({ moduleId, key: 'gradingSchemeId', value: query.grading_scheme.id.toString() }));

                return this._newGradingScheme(query.grading_scheme, query.rule);
            })
        );
    }

    unsetGradingSchemeFromModule(moduleId: string): Observable<any> {
        return this.http.delete(`${this.apiUrl}/modules/${moduleId}/grading-scheme`).pipe(
            tap(() => {
                this._store.dispatch(new SetModuleKeyValue({ moduleId, key: 'gradingSchemeId', value: null }));
            })
        );
    }

    updateGradeCalculation(moduleId: string, value: string): Observable<any> {
        return this.http.patch<GradingSchemeApiServiceRead>(`${this.apiUrl}/graded-modules/${moduleId}`, { rule: value }).pipe(
            map((query) => {
                this._store.dispatch(new SetModuleKeyValue({ moduleId, key: 'gradingSchemeId', value: query.grading_scheme.id.toString() }));

                return this._newGradingScheme(query.grading_scheme, query.rule);
            })
        );
    }
}
